import React from 'react';
import { useTranslation } from 'react-i18next';
import { TooltipHelp, TextAreaLabel, Section, Icon, TranslateDropdown, Card } from '@weezevent/nacre';
import { LANGUAGES } from '../../../../components/translations/translationModal';
import Style from './translations.module.css';
import { aliases } from '../../../../i18n/i18n';

const WidgetTranslationInput = React.memo(({ title = '', tooltip = '', onChangeForm = () => {}, form, selectedLanguage = 'en-GB', translation }) => {
    const { t } = useTranslation();
    const normalizedLang = selectedLanguage.toLowerCase();

    const handleReset = React.useCallback(
        keyToRemove => {
            if (!form?.translations?.wording) return;

            const updatedWording = { ...form.translations.wording };

            if (updatedWording[normalizedLang]) {
                const updatedLangObj = { ...updatedWording[normalizedLang] };
                delete updatedLangObj[keyToRemove];

                updatedWording[normalizedLang] = updatedLangObj;
                onChangeForm('translations', { wording: updatedWording });
            }
        },
        [form, normalizedLang]
    );

    const handleAddTranslation = React.useCallback(
        (key, value) => {
            // We need to initialize translation/wording/selected language if some of those are empty
            const updatedTranslations = { ...form.translations };

            if (!updatedTranslations.wording) {
                updatedTranslations.wording = {};
            }

            if (!updatedTranslations.wording[normalizedLang]) {
                updatedTranslations.wording[normalizedLang] = {};
            }

            updatedTranslations.wording[normalizedLang][key] = value;

            onChangeForm('translations', updatedTranslations);
        },
        [form, normalizedLang]
    );

    return (
        <TextAreaLabel
            style={{ resize: 'none' }}
            label={
                <div className={Style['translation-card-header']}>
                    <div>
                        {title}
                        {tooltip && <TooltipHelp title={tooltip} />}
                    </div>
                    <div className={Style['translation-reset']} onClick={() => handleReset(translation.key)}>
                        <Icon name="Reset" />
                        <div className={Style['reset-label']}>{t('ticket.personal_spaces.translations.reset')}</div>
                    </div>
                </div>
            }
            placeholder={t('ticket.personal_spaces.translations.text-area-placeholder')}
            value={form.translations.wording?.[normalizedLang]?.[translation.key] ?? translation.default_wording[normalizedLang]}
            onChange={e => handleAddTranslation(translation.key, e.target.value)}
        />
    );
});

const WidgetTranslationGroup = React.memo(({ group = null, selectedLanguage = 'en-gb', form, onChangeForm }) => {
    if (!group.translations.length) return null;

    return (
        <Section>
            <div className={Style['group-title']}>{group.label}</div>
            <div className={Style['group-subtitle']}>{group.description}</div>
            <div className={Style['group-content']}>
                {group.translations.map((translation, i) => (
                    <WidgetTranslationInput
                        key={i}
                        translation={translation}
                        title={translation.label}
                        tooltip={translation.description}
                        form={form}
                        onChangeForm={onChangeForm}
                        selectedLanguage={selectedLanguage}
                    />
                ))}
            </div>
        </Section>
    );
});

export default function Translations({ languages, default_language, translations = {}, wording_structure, form, onChangeForm = () => {} }) {
    const { t } = useTranslation();
    const [selectedLanguage, setSelectedLanguage] = React.useState(default_language);

    const languageOptions = React.useMemo(() => {
        return languages.map(lang => {
            const [langPart, regionPart] = lang.split('-');
            return {
                label: LANGUAGES[lang.toLowerCase()],
                value: regionPart ? `${langPart}-${regionPart.toUpperCase()}` : langPart
            };
        });
    }, [languages]);

    const defaultLang = React.useMemo(() => {
        const [langPart, regionPart] = default_language.split('-');
        const defaultLang = regionPart ? `${langPart}-${regionPart.toUpperCase()}` : langPart;

        // Check if default_language is in languageOptions
        if (!languageOptions || languageOptions.some(lang => lang.value === default_language)) {
            return defaultLang;
        }

        // default_language might not match languageOptions value
        // In that case, we check if the alias exists in languageOptions
        if (languageOptions.find(lang => lang.value === aliases[default_language])) {
            return aliases[default_language];
        }

        // If not found, return the first available language option
        return languageOptions[0].value;
    }, [default_language, languageOptions]);

    React.useEffect(() => {
        setSelectedLanguage(defaultLang);
    }, [defaultLang]);

    return (
        <Card>
            <Card.Header>
                <div className={Style['translation-card-header']}>
                    {t('ticket.personal_spaces.translations.title')}
                    <TranslateDropdown defaultValue={defaultLang} languages={languageOptions} onChange={value => setSelectedLanguage(value)} selected={Object.keys(translations)} />
                </div>
            </Card.Header>
            <Card.Content>
                {wording_structure?.map((wording_structure_group, i) => (
                    <WidgetTranslationGroup key={i} group={wording_structure_group} selectedLanguage={selectedLanguage} form={form} onChangeForm={onChangeForm} />
                ))}
            </Card.Content>
        </Card>
    );
}
